"use client";

import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { AnimatePresence, motion, type MotionProps } from "framer-motion";
import { PortableText, type PortableTextComponents } from "@portabletext/react";
import type { PortableTextBlock } from "sanity";

import Chip from "@/components/global/ui/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import DotsBackdrop from "@/components/lp-long/DotsBackdrop";
import { cn } from "@/lib/utils";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PageLPLongSchema } from "@/sanity/pages/PageLPLongTemplate";
import imageUrlBuilder from "@sanity/image-url";
import { dataset, projectId } from "@/sanity/env";

// applies the crop/hotspot set in the Studio, so the CDN delivers the framed portrait
const imageBuilder = imageUrlBuilder({ projectId, dataset });

// framer's div typing clashes with the installed React types on className; keep the cast local
const MotionDiv = motion.div as React.ComponentType<
  React.HTMLAttributes<HTMLDivElement> & MotionProps & { key?: React.Key }
>;

// the title is one text run in Sanity; split it before this word so mobile gets an extra line
const MOBILE_BREAK_BEFORE = "besten";

const teamTitleComponents: PortableTextComponents = {
  ...richTextComponent,
  types: { ...richTextComponent.types, mobileBreak: () => <br className="lg:hidden" /> },
};

function withMobileBreak(blocks: PortableTextBlock[]): PortableTextBlock[] {
  return blocks.map((block) => {
    if (block._type !== "block") return block;
    const children = (block.children as { _type: string; _key: string; text?: string }[]).flatMap((child) => {
      if (child._type !== "span" || typeof child.text !== "string") return [child];
      const at = child.text.indexOf(MOBILE_BREAK_BEFORE);
      if (at <= 0) return [child];
      return [
        { ...child, text: child.text.slice(0, at) },
        { _type: "mobileBreak", _key: `${child._key}-br` },
        { ...child, _key: `${child._key}-after`, text: child.text.slice(at) },
      ];
    });
    return { ...block, children };
  });
}

// bottom-right corner cut (14px), same language as the corner buttons
const CORNER_CUT = "[clip-path:polygon(0_0,100%_0,100%_calc(100%-1.75rem),calc(100%-1.75rem)_100%,0_100%)]";

// how long a tab stays active before auto-advancing to the next one
const AUTOPLAY_DURATION_MS = 4000;

export default function TeamSection({ data, className }: { data: PageLPLongSchema; className?: string }) {
  const items = data?.teamItems ?? [];
  const [active, setActive] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const userPicked = useRef(false);

  // keep the picked tab visible inside the horizontally scrolling row (mobile);
  // only for the click that just happened, not for later autoplay advances
  useEffect(() => {
    if (!userPicked.current) return;
    userPicked.current = false;
    tabRefs.current[active]?.scrollIntoView({
      block: "nearest",
      inline: "center",
      behavior: "smooth",
    });
  }, [active]);

  // auto-advance to the next tab; a manual click resets this timer
  useEffect(() => {
    if (items.length < 2) return;
    const timer = setTimeout(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, AUTOPLAY_DURATION_MS);
    return () => clearTimeout(timer);
  }, [active, items.length]);

  const current = items[active];
  if (!items.length) return null;

  return (
    <section
      id="team"
      className={cn("relative overflow-clip py-[8rem] lg:pt-[17rem] lg:pb-[15rem]", className)}>
      <DotsBackdrop className="-top-[1.25rem]" />
      <DecorationLine
        className="top-0 hidden lg:left-[8.85%] lg:block"
        orientation="vertical"
        style="dark"
      />
      <DecorationLine
        className="top-0 hidden lg:right-[8.85%] lg:block"
        orientation="vertical"
        style="dark"
      />

      <div className="relative container flex flex-col items-center">
        <Chip
          className="prose-p:font-geistmono prose-p:text-[#202223] prose-p:uppercase"
          image={data?.teamChip?.icon}
          text={data?.teamChip?.text}
        />

        {data?.teamTitle && (
          <div className="richtext prose-headings:mb-0 prose-headings:w-[min(112rem,100%)]">
            <PortableText
              value={withMobileBreak(data.teamTitle)}
              components={teamTitleComponents}
            />
          </div>
        )}

        {data?.teamText && (
          <div className="richtext mt-[2rem] px-[2rem] lg:mt-[4rem] lg:px-0 prose-p:w-[min(78.75rem,100%)] prose-p:text-[2.25rem] prose-p:leading-[1.5] prose-p:text-black/60">
            <PortableText
              value={data.teamText}
              components={richTextComponent}
            />
          </div>
        )}

        {/* member panel */}
        <div className="mt-[4rem] w-[min(150rem,100%)] lg:mt-[8rem]">
          <div className="h-[.5rem] w-full bg-[#D9F9B9]" />

          <div className="overflow-hidden bg-white p-[2.25rem]">
            <AnimatePresence
              mode="wait"
              initial={false}>
              <MotionDiv
                key={active}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col lg:flex-row lg:gap-[6.75rem]">
                <div className="flex shrink-0 flex-col gap-[3rem] py-[3rem] lg:w-[64.25rem] lg:p-[3rem]">
                  <div className="flex flex-col gap-[3rem]">
                    <div>
                      <p className="mb-0 font-funnel text-[3rem] leading-[1.4] font-medium text-black lg:text-[4rem]">
                        {current?.name}
                      </p>
                      {current?.role && (
                        <p className="mt-[1rem] mb-0 font-geist text-[2.25rem] leading-[1.4] text-black/70 lg:text-[2.5rem]">
                          {current.role}
                        </p>
                      )}
                    </div>

                    {!!current?.tags?.length && (
                      <ul className="flex flex-wrap gap-[.625rem]">
                        {current.tags.map((tag, i) => (
                          <li
                            key={i}
                            className="rounded-full bg-[#D9F9B9] px-[2rem] py-[1.75rem] font-geistmono text-[1.75rem] leading-none font-medium text-black uppercase">
                            {tag}
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="h-px w-full bg-black/10" />
                  </div>

                  {current?.text && <p className="mb-0 text-[2rem] leading-[1.5] text-black/60">{current.text}</p>}
                </div>

                <div className="relative order-first aspect-[362/418] w-full overflow-hidden lg:order-last lg:aspect-[519/466] lg:h-auto lg:flex-1">
                  {current?.image?.asset?._ref && (
                    <Image
                      className="object-cover object-center"
                      src={imageBuilder.image(current.image).width(1400).auto("format").url()}
                      alt={current?.name ?? ""}
                      fill
                      sizes="(min-width: 993px) 75rem, 100vw"
                    />
                  )}
                  <div
                    className="absolute inset-0 bg-black/10"
                    aria-hidden
                  />
                </div>
              </MotionDiv>
            </AnimatePresence>
          </div>

          {/* tabs */}
          <div
            className="mt-[.75rem] flex gap-[.75rem] overflow-x-auto [scrollbar-width:none] lg:overflow-visible [&::-webkit-scrollbar]:hidden"
            role="tablist">
            {items.map((item, i) => {
              const isActive = i === active;
              return (
                <button
                  key={i}
                  ref={(el) => {
                    tabRefs.current[i] = el;
                  }}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => {
                    userPicked.current = true;
                    setActive(i);
                  }}
                  className={cn(
                    "relative flex shrink-0 items-center justify-center gap-[1rem] px-[3rem] py-[1.75rem] transition-colors duration-300 lg:flex-1",
                    CORNER_CUT,
                    isActive ? "bg-[#D9F9B9]" : "bg-white hover:bg-[#EFFFDF]",
                  )}>
                  <span
                    className={cn(
                      "flex h-[3rem] w-[3rem] items-center justify-center rounded-full border font-geistmono text-[2rem] leading-none text-black/60",
                      isActive ? "border-black/10" : "border-black/20",
                    )}>
                    {i + 1}
                  </span>
                  <span className="font-geistmono text-[2rem] leading-none whitespace-nowrap text-black uppercase">
                    {item?.tab || item?.name}
                  </span>
                  {isActive && (
                    <span
                      className="absolute bottom-0 left-0 h-[2px] w-full overflow-hidden bg-black/10"
                      aria-hidden>
                      <MotionDiv
                        key={active}
                        className="h-full w-full origin-left bg-[#86DB31]"
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: AUTOPLAY_DURATION_MS / 1000, ease: "linear" }}
                      />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
